/**
 * @file     navigation.ts
 * @purpose  Defines the app's navigation taxonomy: categories (buy, rent, sell, learn, tools, about) with their i18n label keys and child route items.
 */
import { featureFlags } from '@/core/config/featureFlags';
import { routes } from './routes';

export type NavigationItem = {
  labelKey: string;
  path: string;
  /** Optional icon identifier, rendered by NavbarMenu when present */
  icon?: 'calculator' | 'map' | 'home' | 'chart' | 'book' | 'person';
  /** cta_click id fired when the item is chosen from the header menu or sheet. */
  ctaId?: string;
};

export type NavigationCategory = {
  key: 'buy' | 'rent' | 'sell' | 'data' | 'learn' | 'tools' | 'blog' | 'about';
  labelKey: string;
  items: NavigationItem[];
  directLink?: string;
};

/** The map search in Rent mode. */
export const FIND_RENTAL_PATH = routes.rent;

/**
 * Rent sits beside Buy and Sell when rentals are on. Rent out your place needs
 * owner listings too; without it the landlord funnel keeps the second slot.
 */
const rentCategory: NavigationCategory = {
  key: 'rent',
  labelKey: 'nav.categories.rent',
  items: [
    { labelKey: 'nav.items.findRental', path: FIND_RENTAL_PATH, icon: 'map', ctaId: 'nav_find_rental' },
    featureFlags.ownerListings
      ? { labelKey: 'nav.items.rentOutYourPlace', path: routes.rentOutYourPlace, icon: 'home', ctaId: 'nav_rent_out' }
      : { labelKey: 'nav.items.findTenants', path: routes.findTenantsLanding, icon: 'home' },
  ],
};

export const navigationCategories: NavigationCategory[] = [
  {
    key: 'buy',
    labelKey: 'nav.categories.buy',
    items: [
      { labelKey: 'nav.items.startMySearch', path: '/deal-finder/start?flow=buyer' },
      { labelKey: 'nav.items.shortTermRentalHomes', path: routes.shortTermRentalHomes },
      { labelKey: 'nav.items.mortgageCalculator', path: routes.mortgageCalculator, icon: 'calculator' },
    ],
    directLink: routes.properties,
  },
  ...(featureFlags.rentals ? [rentCategory] : []),
  {
    key: 'sell',
    labelKey: 'nav.categories.sell',
    items: [
      { labelKey: 'nav.items.getPropertyValue', path: routes.homeValue },
      { labelKey: 'nav.items.shouldISellNow', path: routes.shouldISellNow },
      // Find tenants moves under Rent while rentals are on.
      ...(featureFlags.rentals ? [] : [{ labelKey: 'nav.items.findTenants', path: routes.findTenantsLanding }]),
    ],
    directLink: routes.sellHouse,
  },
  {
    key: 'data',
    labelKey: 'nav.categories.data',
    directLink: routes.marketReport,
    items: [],
  },
  {
    key: 'tools',
    labelKey: 'nav.categories.tools',
    directLink: routes.tools,
    items: [
      { labelKey: 'nav.items.propertyFinder', path: routes.propertyFinder, icon: 'map' },
      { labelKey: 'nav.items.compare', path: routes.compare, icon: 'chart' },
      { labelKey: 'nav.items.smartMatch', path: routes.smartMatch, icon: 'home' },
      { labelKey: 'nav.items.mortgageCalculator', path: routes.mortgageCalculator, icon: 'calculator' },
    ]
  },
  {
    // Blog folds under Academy: the category links straight to the Academy hub
    // and the drawer exposes the blog beneath it.
    key: 'learn',
    labelKey: 'nav.categories.learn',
    directLink: routes.academy,
    items: [
      { labelKey: 'nav.items.blog', path: routes.blog, icon: 'book' },
    ],
  },
  {
    key: 'about',
    labelKey: 'nav.items.aboutRay',
    directLink: routes.aboutRay,
    items: [],
  }
];

export const navbarCategories = navigationCategories;
